/**
 * Linux Installation Guide Page
 * Completely synced with docs/tutorials/linux-install.html
 */

import { useState, useCallback } from 'react';
import { ArrowLeft, ExternalLink, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { Lightbox } from '@/components/docs/Lightbox';
import './docs.scss';

// Types for the Linux install data structure
interface Subsection {
  title: string;
  content: string;
}

interface Step {
  title: string;
  content: string;
  subsections?: Subsection[];
  warning?: string;
  info?: string;
  success?: string;
}

interface TroubleshootingIssue {
  title: string;
  content: string;
}

interface LinuxInstallData {
  pageTitle: string;
  langLabel: string;
  backLink: string;
  tocTitle: string;
  mobileJumpTo: string;
  overview: string;
  requirements: {
    title: string;
    items: string[];
  };
  steps: Step[];
  troubleshooting: {
    title: string;
    issues: TroubleshootingIssue[];
  };
}

// Translations object - exactly matching linux-install.html
const translations: Record<string, LinuxInstallData> = {
  en: {
    pageTitle: "Linux Installation Guide",
    langLabel: "Language:",
    backLink: "← Back to Documentation",
    tocTitle: "Table of Contents",
    mobileJumpTo: "Jump to section...",
    overview: "This guide will walk you through installing Sokuji on Linux systems. Sokuji is available as a .deb package for Debian/Ubuntu-based distributions and as an AppImage for universal compatibility.",
    requirements: {
      title: "System Requirements",
      items: [
        "Ubuntu 20.04 or later, Debian 10 or later, or equivalent distribution",
        "64-bit processor",
        "4GB RAM minimum (8GB recommended)",
        "100MB available disk space",
        "PulseAudio or PipeWire for audio support",
        "Internet connection for AI services"
      ]
    },
    steps: [
      {
        title: "Step 1: Download Sokuji",
        content: `Visit the <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">official GitHub releases page</a> to download the latest version of Sokuji for Linux.`,
        subsections: [
          {
            title: "Available Packages",
            content: `<ul>
              <li><strong>.deb package</strong> - For Debian/Ubuntu based distributions</li>
              <li><strong>.AppImage</strong> - Universal package for all Linux distributions</li>
              <li><strong>.rpm package</strong> - For Fedora/RHEL based distributions (if available)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Step 2: Installing .deb Package (Ubuntu/Debian)",
        content: `If you're using Ubuntu, Debian, or their derivatives, the .deb package is recommended.`,
        subsections: [
          {
            title: "Method 1: Using GUI",
            content: `<ol>
              <li>Open your file manager and navigate to the Downloads folder</li>
              <li>Double-click the <code>sokuji_*.deb</code> file</li>
              <li>Click "Install" in the Software Center that opens</li>
              <li>Enter your password when prompted</li>
            </ol>`
          },
          {
            title: "Method 2: Using Terminal",
            content: `Open Terminal and run the following commands:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            If you encounter dependency issues, fix them with:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Step 3: Installing AppImage (Universal)",
        content: `AppImage works on all Linux distributions and doesn't require installation.`,
        subsections: [
          {
            title: "Setup AppImage",
            content: `<ol>
              <li>Download the AppImage file from the releases page</li>
              <li>Make it executable:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Run the application:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Optional: System Integration",
            content: `To integrate the AppImage with your system:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Now you can run Sokuji from anywhere by typing <code>sokuji</code> in the terminal.`
          }
        ]
      },
      {
        title: "Step 4: First Run Configuration",
        content: `When you launch Sokuji for the first time, you'll need to configure it:`,
        subsections: [
          {
            title: "Initial Setup",
            content: `<ol>
              <li>Launch Sokuji from your application menu or terminal</li>
              <li>Select your preferred interface language</li>
              <li>Configure your AI provider (OpenAI, Google Gemini, etc.)</li>
              <li>Enter your API key for the selected provider</li>
              <li>Select your microphone, and in the <strong>Speech</strong> block choose your speakers or headphones as the <strong>Default playback device</strong>. <strong>Translation the other side hears</strong> goes into the virtual microphone, <code>Sokuji_Virtual_Mic</code>, which you pick as the microphone in your meeting app</li>
              <li>Test the audio to ensure everything works</li>
            </ol>`
          }
        ],
        success: "Sokuji is now ready to use! You can start real-time translation by clicking the start session button."
      }
    ],
    troubleshooting: {
      title: "Troubleshooting",
      issues: [
        {
          title: "Audio Issues",
          content: `If you experience audio problems:
          <ul>
            <li>Ensure PulseAudio or PipeWire is running: <code>systemctl --user status pipewire</code></li>
            <li>Check audio permissions: <code>groups $USER</code> should include 'audio'</li>
            <li>Restart audio service: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Permission Denied",
          content: `If you get "Permission denied" errors:
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Then log out and log back in for the changes to take effect.`
        },
        {
          title: "Missing Dependencies",
          content: `Install required libraries:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage Won't Run",
          content: `If AppImage fails to run, install FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          For newer systems (Ubuntu 22.04+), you might need:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  zh: {
    pageTitle: "Linux 安装指南",
    langLabel: "语言：",
    backLink: "← 返回文档",
    tocTitle: "目录",
    mobileJumpTo: "跳转到章节...",
    overview: "本指南将指导您在 Linux 系统上安装 Sokuji。Sokuji 提供 .deb 包用于 Debian/Ubuntu 发行版，以及 AppImage 用于通用兼容性。",
    requirements: {
      title: "系统要求",
      items: [
        "Ubuntu 20.04 或更高版本，Debian 10 或更高版本，或同等发行版",
        "64 位处理器",
        "最少 4GB RAM（推荐 8GB）",
        "100MB 可用磁盘空间",
        "用于音频支持的 PulseAudio 或 PipeWire",
        "用于 AI 服务的互联网连接"
      ]
    },
    steps: [
      {
        title: "步骤 1：下载 Sokuji",
        content: `访问 <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">官方 GitHub 发布页面</a> 下载最新版本的 Linux 版 Sokuji。`,
        subsections: [
          {
            title: "可用的安装包",
            content: `<ul>
              <li><strong>.deb 包</strong> - 用于基于 Debian/Ubuntu 的发行版</li>
              <li><strong>.AppImage</strong> - 适用于所有 Linux 发行版的通用包</li>
              <li><strong>.rpm 包</strong> - 用于基于 Fedora/RHEL 的发行版（如果可用）</li>
            </ul>`
          }
        ]
      },
      {
        title: "步骤 2：安装 .deb 包（Ubuntu/Debian）",
        content: `如果您使用 Ubuntu、Debian 或其衍生版，推荐使用 .deb 包。`,
        subsections: [
          {
            title: "方法 1：使用 GUI",
            content: `<ol>
              <li>打开文件管理器并导航到下载文件夹</li>
              <li>双击 <code>sokuji_*.deb</code> 文件</li>
              <li>在打开的软件中心中点击"安装"</li>
              <li>在提示时输入您的密码</li>
            </ol>`
          },
          {
            title: "方法 2：使用终端",
            content: `打开终端并运行以下命令：
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            如果遇到依赖问题，使用以下命令修复：
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "步骤 3：安装 AppImage（通用）",
        content: `AppImage 适用于所有 Linux 发行版，无需安装。`,
        subsections: [
          {
            title: "设置 AppImage",
            content: `<ol>
              <li>从发布页面下载 AppImage 文件</li>
              <li>使其可执行：
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>运行应用程序：
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "可选：系统集成",
            content: `要将 AppImage 与系统集成：
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            现在您可以在终端的任何位置输入 <code>sokuji</code> 来运行 Sokuji。`
          }
        ]
      },
      {
        title: "步骤 4：首次运行配置",
        content: `首次启动 Sokuji 时，您需要进行配置：`,
        subsections: [
          {
            title: "初始设置",
            content: `<ol>
              <li>从应用程序菜单或终端启动 Sokuji</li>
              <li>选择您喜欢的界面语言</li>
              <li>配置您的 AI 提供商（OpenAI、Google Gemini 等）</li>
              <li>输入所选提供商的 API 密钥</li>
              <li>选择您的麦克风，并在<strong>语音</strong>区块中把<strong>默认播放设备</strong>设为您的扬声器或耳机。<strong>对方听到的翻译</strong>会送进虚拟麦克风 <code>Sokuji_Virtual_Mic</code>，请在会议应用中把它选为麦克风</li>
              <li>测试音频以确保一切正常</li>
            </ol>`
          }
        ],
        success: "Sokuji 现在已准备就绪！您可以通过点击开始会话按钮开始实时翻译。"
      }
    ],
    troubleshooting: {
      title: "故障排除",
      issues: [
        {
          title: "音频问题",
          content: `如果遇到音频问题：
          <ul>
            <li>确保 PulseAudio 或 PipeWire 正在运行：<code>systemctl --user status pipewire</code></li>
            <li>检查音频权限：<code>groups $USER</code> 应包含 'audio'</li>
            <li>重启音频服务：<code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "权限拒绝",
          content: `如果收到"Permission denied"错误：
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          然后注销并重新登录以使更改生效。`
        },
        {
          title: "缺少依赖项",
          content: `安装所需的库：
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage 无法运行",
          content: `如果 AppImage 无法运行，请安装 FUSE：
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          对于较新的系统（Ubuntu 22.04+），您可能需要：
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  ja: {
    pageTitle: "Linux インストールガイド",
    langLabel: "言語：",
    backLink: "← ドキュメントに戻る",
    tocTitle: "目次",
    mobileJumpTo: "セクションにジャンプ...",
    overview: "このガイドでは、Linux システムに Sokuji をインストールする方法を説明します。Sokuji は Debian/Ubuntu ベースのディストリビューション用の .deb パッケージと、汎用互換性のための AppImage として利用可能です。",
    requirements: {
      title: "システム要件",
      items: [
        "Ubuntu 20.04 以降、Debian 10 以降、または同等のディストリビューション",
        "64 ビットプロセッサー",
        "最小 4GB RAM（推奨 8GB）",
        "100MB の空きディスク容量",
        "音声サポート用の PulseAudio または PipeWire",
        "AI サービス用のインターネット接続"
      ]
    },
    steps: [
      {
        title: "ステップ 1：Sokuji をダウンロード",
        content: `<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">公式 GitHub リリースページ</a>にアクセスして、Linux 用の Sokuji の最新バージョンをダウンロードします。`,
        subsections: [
          {
            title: "利用可能なパッケージ",
            content: `<ul>
              <li><strong>.deb パッケージ</strong> - Debian/Ubuntu ベースのディストリビューション用</li>
              <li><strong>.AppImage</strong> - すべての Linux ディストリビューション用の汎用パッケージ</li>
              <li><strong>.rpm パッケージ</strong> - Fedora/RHEL ベースのディストリビューション用（利用可能な場合）</li>
            </ul>`
          }
        ]
      },
      {
        title: "ステップ 2：.deb パッケージのインストール（Ubuntu/Debian）",
        content: `Ubuntu、Debian、またはその派生版を使用している場合は、.deb パッケージをお勧めします。`,
        subsections: [
          {
            title: "方法 1：GUI を使用",
            content: `<ol>
              <li>ファイルマネージャーを開き、ダウンロードフォルダーに移動</li>
              <li><code>sokuji_*.deb</code> ファイルをダブルクリック</li>
              <li>開いたソフトウェアセンターで「インストール」をクリック</li>
              <li>要求されたらパスワードを入力</li>
            </ol>`
          },
          {
            title: "方法 2：ターミナルを使用",
            content: `ターミナルを開いて以下のコマンドを実行：
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            依存関係の問題が発生した場合は、以下で修正：
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "ステップ 3：AppImage のインストール（汎用）",
        content: `AppImage はすべての Linux ディストリビューションで動作し、インストールは不要です。`,
        subsections: [
          {
            title: "AppImage のセットアップ",
            content: `<ol>
              <li>リリースページから AppImage ファイルをダウンロード</li>
              <li>実行可能にする：
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>アプリケーションを実行：
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "オプション：システム統合",
            content: `AppImage をシステムに統合するには：
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            これで、ターミナルの任意の場所で <code>sokuji</code> と入力して Sokuji を実行できます。`
          }
        ]
      },
      {
        title: "ステップ 4：初回起動設定",
        content: `Sokuji を初めて起動するときは、設定が必要です：`,
        subsections: [
          {
            title: "初期設定",
            content: `<ol>
              <li>アプリケーションメニューまたはターミナルから Sokuji を起動</li>
              <li>希望のインターフェース言語を選択</li>
              <li>AI プロバイダー（OpenAI、Google Gemini など）を設定</li>
              <li>選択したプロバイダーの API キーを入力</li>
              <li>マイクを選択し、<strong>音声</strong>ブロックで<strong>既定の再生デバイス</strong>にスピーカーまたはヘッドフォンを選択。<strong>相手に聞こえる翻訳</strong>は仮想マイク <code>Sokuji_Virtual_Mic</code> に送られるので、会議アプリでこれをマイクとして選びます</li>
              <li>オーディオをテストして、すべてが正常に動作することを確認</li>
            </ol>`
          }
        ],
        success: "Sokuji の準備が整いました！セッション開始ボタンをクリックしてリアルタイム翻訳を開始できます。"
      }
    ],
    troubleshooting: {
      title: "トラブルシューティング",
      issues: [
        {
          title: "音声の問題",
          content: `音声に問題がある場合：
          <ul>
            <li>PulseAudio または PipeWire が実行中であることを確認：<code>systemctl --user status pipewire</code></li>
            <li>音声権限を確認：<code>groups $USER</code> に 'audio' が含まれているはず</li>
            <li>音声サービスを再起動：<code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "権限拒否",
          content: `「Permission denied」エラーが発生した場合：
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          その後、ログアウトして再度ログインして変更を適用します。`
        },
        {
          title: "依存関係の不足",
          content: `必要なライブラリをインストール：
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage が実行できない",
          content: `AppImage が実行できない場合は、FUSE をインストール：
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          新しいシステム（Ubuntu 22.04+）では、次が必要な場合があります：
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  ko: {
    pageTitle: "Linux 설치 가이드",
    langLabel: "언어:",
    backLink: "← 문서로 돌아가기",
    tocTitle: "목차",
    mobileJumpTo: "섹션으로 이동...",
    overview: "이 가이드는 Linux 시스템에 Sokuji를 설치하는 방법을 안내합니다. Sokuji는 Debian/Ubuntu 기반 배포판용 .deb 패키지와 범용 호환성을 위한 AppImage로 제공됩니다.",
    requirements: {
      title: "시스템 요구 사항",
      items: [
        "Ubuntu 20.04 이상, Debian 10 이상 또는 동등한 배포판",
        "64비트 프로세서",
        "최소 4GB RAM (8GB 권장)",
        "100MB 사용 가능한 디스크 공간",
        "오디오 지원을 위한 PulseAudio 또는 PipeWire",
        "AI 서비스를 위한 인터넷 연결"
      ]
    },
    steps: [
      {
        title: "단계 1: Sokuji 다운로드",
        content: `<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">공식 GitHub 릴리스 페이지</a>를 방문하여 Linux용 Sokuji 최신 버전을 다운로드하세요.`,
        subsections: [
          {
            title: "사용 가능한 패키지",
            content: `<ul>
              <li><strong>.deb 패키지</strong> - Debian/Ubuntu 기반 배포판용</li>
              <li><strong>.AppImage</strong> - 모든 Linux 배포판을 위한 범용 패키지</li>
              <li><strong>.rpm 패키지</strong> - Fedora/RHEL 기반 배포판용 (사용 가능한 경우)</li>
            </ul>`
          }
        ]
      },
      {
        title: "단계 2: .deb 패키지 설치 (Ubuntu/Debian)",
        content: `Ubuntu, Debian 또는 그 파생 버전을 사용하는 경우 .deb 패키지를 권장합니다.`,
        subsections: [
          {
            title: "방법 1: GUI 사용",
            content: `<ol>
              <li>파일 관리자를 열고 다운로드 폴더로 이동</li>
              <li><code>sokuji_*.deb</code> 파일을 더블 클릭</li>
              <li>열린 소프트웨어 센터에서 "설치"를 클릭</li>
              <li>요청 시 비밀번호 입력</li>
            </ol>`
          },
          {
            title: "방법 2: 터미널 사용",
            content: `터미널을 열고 다음 명령을 실행:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            종속성 문제가 발생하면 다음으로 수정:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "단계 3: AppImage 설치 (범용)",
        content: `AppImage는 모든 Linux 배포판에서 작동하며 설치가 필요하지 않습니다.`,
        subsections: [
          {
            title: "AppImage 설정",
            content: `<ol>
              <li>릴리스 페이지에서 AppImage 파일 다운로드</li>
              <li>실행 가능하게 만들기:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>애플리케이션 실행:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "선택사항: 시스템 통합",
            content: `AppImage를 시스템과 통합하려면:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            이제 터미널 어디에서나 <code>sokuji</code>를 입력하여 Sokuji를 실행할 수 있습니다.`
          }
        ]
      },
      {
        title: "단계 4: 첫 실행 구성",
        content: `Sokuji를 처음 실행할 때 구성이 필요합니다:`,
        subsections: [
          {
            title: "초기 설정",
            content: `<ol>
              <li>애플리케이션 메뉴 또는 터미널에서 Sokuji 실행</li>
              <li>선호하는 인터페이스 언어 선택</li>
              <li>AI 제공자 구성 (OpenAI, Google Gemini 등)</li>
              <li>선택한 제공자의 API 키 입력</li>
              <li>마이크를 선택하고, <strong>음성</strong> 블록에서 <strong>기본 재생 장치</strong>를 스피커나 헤드폰으로 선택. <strong>상대방에게 들리는 번역</strong>은 가상 마이크 <code>Sokuji_Virtual_Mic</code>으로 보내지므로, 회의 앱에서 이것을 마이크로 선택합니다</li>
              <li>오디오를 테스트하여 모든 것이 작동하는지 확인</li>
            </ol>`
          }
        ],
        success: "이제 Sokuji를 사용할 준비가 되었습니다! 마이크 버튼을 클릭하여 실시간 번역을 시작할 수 있습니다."
      }
    ],
    troubleshooting: {
      title: "문제 해결",
      issues: [
        {
          title: "오디오 문제",
          content: `오디오 문제가 발생하는 경우:
          <ul>
            <li>PulseAudio 또는 PipeWire가 실행 중인지 확인: <code>systemctl --user status pipewire</code></li>
            <li>오디오 권한 확인: <code>groups $USER</code>에 'audio'가 포함되어야 함</li>
            <li>오디오 서비스 재시작: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "권한 거부",
          content: `"Permission denied" 오류가 발생하면:
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          그런 다음 로그아웃하고 다시 로그인하여 변경 사항을 적용하세요.`
        },
        {
          title: "누락된 종속성",
          content: `필요한 라이브러리 설치:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage가 실행되지 않음",
          content: `AppImage가 실행되지 않으면 FUSE 설치:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          최신 시스템 (Ubuntu 22.04+)의 경우 다음이 필요할 수 있습니다:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  es: {
    pageTitle: "Guia de instalacion en Linux",
    langLabel: "Idioma:",
    backLink: "← Volver a la documentacion",
    tocTitle: "Tabla de contenidos",
    mobileJumpTo: "Ir a seccion...",
    overview: "Esta guia le ayudara a instalar Sokuji en sistemas Linux. Sokuji esta disponible como paquete .deb para distribuciones basadas en Debian/Ubuntu y como AppImage para compatibilidad universal.",
    requirements: {
      title: "Requisitos del sistema",
      items: [
        "Ubuntu 20.04 o posterior, Debian 10 o posterior, o distribucion equivalente",
        "Procesador de 64 bits",
        "4GB de RAM minimo (8GB recomendado)",
        "100MB de espacio en disco disponible",
        "PulseAudio o PipeWire para soporte de audio",
        "Conexion a internet para servicios de IA"
      ]
    },
    steps: [
      {
        title: "Paso 1: Descargar Sokuji",
        content: `Visite la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lanzamientos en GitHub</a> para descargar la ultima version de Sokuji para Linux.`,
        subsections: [
          {
            title: "Paquetes disponibles",
            content: `<ul>
              <li><strong>Paquete .deb</strong> - Para distribuciones basadas en Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Paquete universal para todas las distribuciones Linux</li>
              <li><strong>Paquete .rpm</strong> - Para distribuciones basadas en Fedora/RHEL (si esta disponible)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Paso 2: Instalar paquete .deb (Ubuntu/Debian)",
        content: `Si usa Ubuntu, Debian o sus derivados, se recomienda el paquete .deb.`,
        subsections: [
          {
            title: "Metodo 1: Usando la interfaz grafica",
            content: `<ol>
              <li>Abra su administrador de archivos y navegue a la carpeta de Descargas</li>
              <li>Haga doble clic en el archivo <code>sokuji_*.deb</code></li>
              <li>Haga clic en "Instalar" en el Centro de Software que se abre</li>
              <li>Ingrese su contrasena cuando se le solicite</li>
            </ol>`
          },
          {
            title: "Metodo 2: Usando la terminal",
            content: `Abra la Terminal y ejecute los siguientes comandos:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Si encuentra problemas de dependencias, corrija con:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Paso 3: Instalar AppImage (Universal)",
        content: `AppImage funciona en todas las distribuciones Linux y no requiere instalacion.`,
        subsections: [
          {
            title: "Configurar AppImage",
            content: `<ol>
              <li>Descargue el archivo AppImage de la pagina de lanzamientos</li>
              <li>Hagalo ejecutable:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Ejecute la aplicacion:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Opcional: Integracion con el sistema",
            content: `Para integrar la AppImage con su sistema:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Ahora puede ejecutar Sokuji desde cualquier lugar escribiendo <code>sokuji</code> en la terminal.`
          }
        ]
      },
      {
        title: "Paso 4: Configuracion del primer inicio",
        content: `Al iniciar Sokuji por primera vez, necesitara configurarlo:`,
        subsections: [
          {
            title: "Configuracion inicial",
            content: `<ol>
              <li>Inicie Sokuji desde el menu de aplicaciones o la terminal</li>
              <li>Seleccione su idioma de interfaz preferido</li>
              <li>Configure su proveedor de IA (OpenAI, Google Gemini, etc.)</li>
              <li>Ingrese su clave API para el proveedor seleccionado</li>
              <li>Seleccione su micrófono y, en el bloque <strong>Voz</strong>, elija sus altavoces o auriculares como <strong>Dispositivo de reproducción predeterminado</strong>. <strong>Traducción que oye la otra parte</strong> va al micrófono virtual <code>Sokuji_Virtual_Mic</code>, que usted elige como micrófono en su aplicación de reuniones</li>
              <li>Pruebe el audio para asegurarse de que todo funcione</li>
            </ol>`
          }
        ],
        success: "Sokuji esta listo para usar. Puede iniciar la traduccion en tiempo real haciendo clic en el boton de inicio de sesion."
      }
    ],
    troubleshooting: {
      title: "Solucion de problemas",
      issues: [
        {
          title: "Problemas de audio",
          content: `Si experimenta problemas de audio:
          <ul>
            <li>Asegurese de que PulseAudio o PipeWire este ejecutandose: <code>systemctl --user status pipewire</code></li>
            <li>Verifique los permisos de audio: <code>groups $USER</code> deberia incluir 'audio'</li>
            <li>Reinicie el servicio de audio: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Permiso denegado",
          content: `Si obtiene errores de "Permission denied":
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Luego cierre sesion y vuelva a iniciarla para que los cambios surtan efecto.`
        },
        {
          title: "Dependencias faltantes",
          content: `Instale las bibliotecas necesarias:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage no se ejecuta",
          content: `Si AppImage no se ejecuta, instale FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Para sistemas mas nuevos (Ubuntu 22.04+), podria necesitar:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  fr: {
    pageTitle: "Guide d'installation Linux",
    langLabel: "Langue :",
    backLink: "← Retour a la documentation",
    tocTitle: "Table des matieres",
    mobileJumpTo: "Aller a la section...",
    overview: "Ce guide vous accompagnera dans l'installation de Sokuji sur les systemes Linux. Sokuji est disponible sous forme de paquet .deb pour les distributions basees sur Debian/Ubuntu et en tant qu'AppImage pour une compatibilite universelle.",
    requirements: {
      title: "Configuration requise",
      items: [
        "Ubuntu 20.04 ou ulterieur, Debian 10 ou ulterieur, ou distribution equivalente",
        "Processeur 64 bits",
        "4 Go de RAM minimum (8 Go recommandes)",
        "100 Mo d'espace disque disponible",
        "PulseAudio ou PipeWire pour le support audio",
        "Connexion Internet pour les services d'IA"
      ]
    },
    steps: [
      {
        title: "Etape 1 : Telecharger Sokuji",
        content: `Visitez la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">page officielle des versions sur GitHub</a> pour telecharger la derniere version de Sokuji pour Linux.`,
        subsections: [
          {
            title: "Paquets disponibles",
            content: `<ul>
              <li><strong>Paquet .deb</strong> - Pour les distributions basees sur Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Paquet universel pour toutes les distributions Linux</li>
              <li><strong>Paquet .rpm</strong> - Pour les distributions basees sur Fedora/RHEL (si disponible)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Etape 2 : Installer le paquet .deb (Ubuntu/Debian)",
        content: `Si vous utilisez Ubuntu, Debian ou leurs derives, le paquet .deb est recommande.`,
        subsections: [
          {
            title: "Methode 1 : Utilisation de l'interface graphique",
            content: `<ol>
              <li>Ouvrez votre gestionnaire de fichiers et naviguez vers le dossier Telechargements</li>
              <li>Double-cliquez sur le fichier <code>sokuji_*.deb</code></li>
              <li>Cliquez sur "Installer" dans le Centre de logiciels qui s'ouvre</li>
              <li>Saisissez votre mot de passe lorsque demande</li>
            </ol>`
          },
          {
            title: "Methode 2 : Utilisation du terminal",
            content: `Ouvrez le Terminal et executez les commandes suivantes :
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Si vous rencontrez des problemes de dependances, corrigez-les avec :
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Etape 3 : Installer l'AppImage (Universel)",
        content: `AppImage fonctionne sur toutes les distributions Linux et ne necessite pas d'installation.`,
        subsections: [
          {
            title: "Configurer l'AppImage",
            content: `<ol>
              <li>Telechargez le fichier AppImage depuis la page des versions</li>
              <li>Rendez-le executable :
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Executez l'application :
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Optionnel : Integration systeme",
            content: `Pour integrer l'AppImage a votre systeme :
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Vous pouvez maintenant executer Sokuji depuis n'importe ou en tapant <code>sokuji</code> dans le terminal.`
          }
        ]
      },
      {
        title: "Etape 4 : Configuration du premier lancement",
        content: `Lorsque vous lancez Sokuji pour la premiere fois, vous devrez le configurer :`,
        subsections: [
          {
            title: "Configuration initiale",
            content: `<ol>
              <li>Lancez Sokuji depuis le menu des applications ou le terminal</li>
              <li>Selectionnez votre langue d'interface preferee</li>
              <li>Configurez votre fournisseur d'IA (OpenAI, Google Gemini, etc.)</li>
              <li>Saisissez votre cle API pour le fournisseur selectionne</li>
              <li>Sélectionnez votre microphone et, dans le bloc <strong>Voix</strong>, choisissez vos haut-parleurs ou votre casque comme <strong>Appareil de lecture par défaut</strong>. <strong>Traduction entendue par l'autre</strong> part dans le micro virtuel <code>Sokuji_Virtual_Mic</code>, que vous choisissez comme microphone dans votre application de réunion</li>
              <li>Testez l'audio pour vous assurer que tout fonctionne</li>
            </ol>`
          }
        ],
        success: "Sokuji est maintenant pret a l'emploi ! Vous pouvez demarrer la traduction en temps reel en cliquant sur le bouton de demarrage de session."
      }
    ],
    troubleshooting: {
      title: "Depannage",
      issues: [
        {
          title: "Problemes audio",
          content: `Si vous rencontrez des problemes audio :
          <ul>
            <li>Assurez-vous que PulseAudio ou PipeWire est en cours d'execution : <code>systemctl --user status pipewire</code></li>
            <li>Verifiez les permissions audio : <code>groups $USER</code> devrait inclure 'audio'</li>
            <li>Redemarrez le service audio : <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Permission refusee",
          content: `Si vous obtenez des erreurs "Permission denied" :
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Puis deconnectez-vous et reconnectez-vous pour que les changements prennent effet.`
        },
        {
          title: "Dependances manquantes",
          content: `Installez les bibliotheques requises :
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "L'AppImage ne s'execute pas",
          content: `Si l'AppImage ne se lance pas, installez FUSE :
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Pour les systemes plus recents (Ubuntu 22.04+), vous pourriez avoir besoin de :
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  ar: {
    pageTitle: "دليل تثبيت Linux",
    langLabel: "اللغة:",
    backLink: "← العودة الى التوثيق",
    tocTitle: "جدول المحتويات",
    mobileJumpTo: "الانتقال الى قسم...",
    overview: "سيرشدك هذا الدليل خلال تثبيت Sokuji على انظمة Linux. يتوفر Sokuji كحزمة .deb لتوزيعات Debian/Ubuntu وكـ AppImage للتوافق الشامل.",
    requirements: {
      title: "متطلبات النظام",
      items: [
        "Ubuntu 20.04 او احدث، Debian 10 او احدث، او توزيعة مكافئة",
        "معالج 64 بت",
        "4 جيجابايت رام كحد ادنى (8 جيجابايت موصى بها)",
        "100 ميجابايت مساحة قرص متاحة",
        "PulseAudio او PipeWire لدعم الصوت",
        "اتصال بالانترنت لخدمات الذكاء الاصطناعي"
      ]
    },
    steps: [
      {
        title: "الخطوة 1: تحميل Sokuji",
        content: `قم بزيارة <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">صفحة الاصدارات الرسمية على GitHub</a> لتحميل احدث اصدار من Sokuji لنظام Linux.`,
        subsections: [
          {
            title: "الحزم المتاحة",
            content: `<ul>
              <li><strong>حزمة .deb</strong> - لتوزيعات Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - حزمة شاملة لجميع توزيعات Linux</li>
              <li><strong>حزمة .rpm</strong> - لتوزيعات Fedora/RHEL (اذا كانت متاحة)</li>
            </ul>`
          }
        ]
      },
      {
        title: "الخطوة 2: تثبيت حزمة .deb (Ubuntu/Debian)",
        content: `اذا كنت تستخدم Ubuntu او Debian او مشتقاتها، يوصى بحزمة .deb.`,
        subsections: [
          {
            title: "الطريقة 1: باستخدام الواجهة الرسومية",
            content: `<ol>
              <li>افتح مدير الملفات وانتقل الى مجلد التنزيلات</li>
              <li>انقر نقرا مزدوجا على ملف <code>sokuji_*.deb</code></li>
              <li>انقر على "تثبيت" في مركز البرامج الذي يفتح</li>
              <li>ادخل كلمة المرور عند المطالبة</li>
            </ol>`
          },
          {
            title: "الطريقة 2: باستخدام الطرفية",
            content: `افتح الطرفية وقم بتشغيل الاوامر التالية:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            اذا واجهت مشاكل في التبعيات، اصلحها بـ:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "الخطوة 3: تثبيت AppImage (شامل)",
        content: `يعمل AppImage على جميع توزيعات Linux ولا يتطلب تثبيتا.`,
        subsections: [
          {
            title: "اعداد AppImage",
            content: `<ol>
              <li>حمل ملف AppImage من صفحة الاصدارات</li>
              <li>اجعله قابلا للتنفيذ:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>شغل التطبيق:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "اختياري: التكامل مع النظام",
            content: `لدمج AppImage مع نظامك:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            الآن يمكنك تشغيل Sokuji من اي مكان بكتابة <code>sokuji</code> في الطرفية.`
          }
        ]
      },
      {
        title: "الخطوة 4: تكوين التشغيل الاول",
        content: `عند تشغيل Sokuji لاول مرة، ستحتاج الى تكوينه:`,
        subsections: [
          {
            title: "الاعداد الاولي",
            content: `<ol>
              <li>شغل Sokuji من قائمة التطبيقات او الطرفية</li>
              <li>حدد لغة الواجهة المفضلة</li>
              <li>قم بتكوين مزود الذكاء الاصطناعي (OpenAI، Google Gemini، الخ.)</li>
              <li>ادخل مفتاح API للمزود المحدد</li>
              <li>حدد الميكروفون، وفي قسم <strong>الكلام</strong> اختر مكبرات الصوت أو السماعات كـ <strong>جهاز التشغيل الافتراضي</strong>. تذهب <strong>الترجمة التي يسمعها الطرف الآخر</strong> إلى الميكروفون الافتراضي <code dir="ltr">Sokuji_Virtual_Mic</code>، الذي تختاره كميكروفون في تطبيق الاجتماعات</li>
              <li>اختبر الصوت للتاكد من ان كل شيء يعمل</li>
            </ol>`
          }
        ],
        success: "Sokuji جاهز الآن للاستخدام! يمكنك بدء الترجمة في الوقت الفعلي بالنقر على زر بدء الجلسة."
      }
    ],
    troubleshooting: {
      title: "استكشاف الاخطاء واصلاحها",
      issues: [
        {
          title: "مشاكل الصوت",
          content: `اذا واجهت مشاكل في الصوت:
          <ul>
            <li>تاكد من ان PulseAudio او PipeWire يعمل: <code>systemctl --user status pipewire</code></li>
            <li>تحقق من اذونات الصوت: <code>groups $USER</code> يجب ان يتضمن 'audio'</li>
            <li>اعد تشغيل خدمة الصوت: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "رفض الاذن",
          content: `اذا حصلت على اخطاء "Permission denied":
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          ثم سجل الخروج واعد تسجيل الدخول لتطبيق التغييرات.`
        },
        {
          title: "تبعيات مفقودة",
          content: `ثبت المكتبات المطلوبة:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage لا يعمل",
          content: `اذا فشل AppImage في التشغيل، ثبت FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          للانظمة الاحدث (Ubuntu 22.04+)، قد تحتاج الى:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  pt: {
    pageTitle: "Guia de Instalacao no Linux",
    langLabel: "Idioma:",
    backLink: "← Voltar a documentacao",
    tocTitle: "Indice",
    mobileJumpTo: "Ir para secao...",
    overview: "Este guia orientara voce na instalacao do Sokuji em sistemas Linux. O Sokuji esta disponivel como pacote .deb para distribuicoes baseadas em Debian/Ubuntu e como AppImage para compatibilidade universal.",
    requirements: {
      title: "Requisitos do Sistema",
      items: [
        "Ubuntu 20.04 ou posterior, Debian 10 ou posterior, ou distribuicao equivalente",
        "Processador de 64 bits",
        "4GB de RAM minimo (8GB recomendado)",
        "100MB de espaco em disco disponivel",
        "PulseAudio ou PipeWire para suporte de audio",
        "Conexao com a internet para servicos de IA"
      ]
    },
    steps: [
      {
        title: "Passo 1: Baixar o Sokuji",
        content: `Visite a <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lancamentos no GitHub</a> para baixar a versao mais recente do Sokuji para Linux.`,
        subsections: [
          {
            title: "Pacotes disponiveis",
            content: `<ul>
              <li><strong>Pacote .deb</strong> - Para distribuicoes baseadas em Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Pacote universal para todas as distribuicoes Linux</li>
              <li><strong>Pacote .rpm</strong> - Para distribuicoes baseadas em Fedora/RHEL (se disponivel)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Passo 2: Instalar pacote .deb (Ubuntu/Debian)",
        content: `Se voce esta usando Ubuntu, Debian ou seus derivados, o pacote .deb e recomendado.`,
        subsections: [
          {
            title: "Metodo 1: Usando a interface grafica",
            content: `<ol>
              <li>Abra o gerenciador de arquivos e navegue ate a pasta Downloads</li>
              <li>Clique duas vezes no arquivo <code>sokuji_*.deb</code></li>
              <li>Clique em "Instalar" na Central de Software que abre</li>
              <li>Digite sua senha quando solicitado</li>
            </ol>`
          },
          {
            title: "Metodo 2: Usando o terminal",
            content: `Abra o Terminal e execute os seguintes comandos:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Se encontrar problemas de dependencia, corrija com:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Passo 3: Instalar AppImage (Universal)",
        content: `O AppImage funciona em todas as distribuicoes Linux e nao requer instalacao.`,
        subsections: [
          {
            title: "Configurar AppImage",
            content: `<ol>
              <li>Baixe o arquivo AppImage da pagina de lancamentos</li>
              <li>Torne-o executavel:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Execute o aplicativo:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Opcional: Integracao com o sistema",
            content: `Para integrar o AppImage com seu sistema:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Agora voce pode executar o Sokuji de qualquer lugar digitando <code>sokuji</code> no terminal.`
          }
        ]
      },
      {
        title: "Passo 4: Configuracao da primeira execucao",
        content: `Ao iniciar o Sokuji pela primeira vez, voce precisara configura-lo:`,
        subsections: [
          {
            title: "Configuracao inicial",
            content: `<ol>
              <li>Inicie o Sokuji pelo menu de aplicativos ou terminal</li>
              <li>Selecione seu idioma de interface preferido</li>
              <li>Configure seu provedor de IA (OpenAI, Google Gemini, etc.)</li>
              <li>Insira sua chave API para o provedor selecionado</li>
              <li>Selecione seu microfone e, no bloco <strong>Voz</strong>, escolha seus alto-falantes ou fones de ouvido como <strong>Dispositivo de reprodução padrão</strong>. <strong>Tradução que o outro lado ouve</strong> vai para o microfone virtual <code>Sokuji_Virtual_Mic</code>, que você escolhe como microfone no aplicativo de reunião</li>
              <li>Teste o audio para garantir que tudo funcione</li>
            </ol>`
          }
        ],
        success: "O Sokuji esta pronto para uso! Voce pode iniciar a traducao em tempo real clicando no botao de inicio de sessao."
      }
    ],
    troubleshooting: {
      title: "Solucao de Problemas",
      issues: [
        {
          title: "Problemas de audio",
          content: `Se voce tiver problemas de audio:
          <ul>
            <li>Certifique-se de que o PulseAudio ou PipeWire esteja em execucao: <code>systemctl --user status pipewire</code></li>
            <li>Verifique as permissoes de audio: <code>groups $USER</code> deve incluir 'audio'</li>
            <li>Reinicie o servico de audio: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Permissao negada",
          content: `Se voce receber erros de "Permission denied":
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Em seguida, faca logout e login novamente para que as alteracoes entrem em vigor.`
        },
        {
          title: "Dependencias ausentes",
          content: `Instale as bibliotecas necessarias:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage nao executa",
          content: `Se o AppImage nao executar, instale o FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Para sistemas mais novos (Ubuntu 22.04+), voce pode precisar de:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  ru: {
    pageTitle: "Руководство по установке на Linux",
    langLabel: "Язык:",
    backLink: "← Назад к документации",
    tocTitle: "Содержание",
    mobileJumpTo: "Перейти к разделу...",
    overview: "Это руководство поможет вам установить Sokuji на системы Linux. Sokuji доступен как пакет .deb для дистрибутивов на базе Debian/Ubuntu и как AppImage для универсальной совместимости.",
    requirements: {
      title: "Системные требования",
      items: [
        "Ubuntu 20.04 или новее, Debian 10 или новее, или эквивалентный дистрибутив",
        "64-разрядный процессор",
        "Минимум 4 ГБ оперативной памяти (рекомендуется 8 ГБ)",
        "100 МБ свободного дискового пространства",
        "PulseAudio или PipeWire для поддержки звука",
        "Подключение к интернету для служб ИИ"
      ]
    },
    steps: [
      {
        title: "Шаг 1: Скачать Sokuji",
        content: `Посетите <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">официальную страницу релизов на GitHub</a>, чтобы скачать последнюю версию Sokuji для Linux.`,
        subsections: [
          {
            title: "Доступные пакеты",
            content: `<ul>
              <li><strong>Пакет .deb</strong> - Для дистрибутивов на базе Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Универсальный пакет для всех дистрибутивов Linux</li>
              <li><strong>Пакет .rpm</strong> - Для дистрибутивов на базе Fedora/RHEL (если доступен)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Шаг 2: Установка пакета .deb (Ubuntu/Debian)",
        content: `Если вы используете Ubuntu, Debian или их производные, рекомендуется пакет .deb.`,
        subsections: [
          {
            title: "Способ 1: Через графический интерфейс",
            content: `<ol>
              <li>Откройте файловый менеджер и перейдите в папку Загрузки</li>
              <li>Дважды щелкните по файлу <code>sokuji_*.deb</code></li>
              <li>Нажмите «Установить» в открывшемся Центре приложений</li>
              <li>Введите пароль при запросе</li>
            </ol>`
          },
          {
            title: "Способ 2: Через терминал",
            content: `Откройте Терминал и выполните следующие команды:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Если возникнут проблемы с зависимостями, исправьте их командой:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Шаг 3: Установка AppImage (Универсальный)",
        content: `AppImage работает во всех дистрибутивах Linux и не требует установки.`,
        subsections: [
          {
            title: "Настройка AppImage",
            content: `<ol>
              <li>Скачайте файл AppImage со страницы релизов</li>
              <li>Сделайте его исполняемым:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Запустите приложение:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Необязательно: Системная интеграция",
            content: `Для интеграции AppImage с системой:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Теперь вы можете запускать Sokuji из любого места, введя <code>sokuji</code> в терминале.`
          }
        ]
      },
      {
        title: "Шаг 4: Настройка первого запуска",
        content: `При первом запуске Sokuji необходимо его настроить:`,
        subsections: [
          {
            title: "Начальная настройка",
            content: `<ol>
              <li>Запустите Sokuji из меню приложений или терминала</li>
              <li>Выберите предпочитаемый язык интерфейса</li>
              <li>Настройте поставщика ИИ (OpenAI, Google Gemini и т.д.)</li>
              <li>Введите API-ключ для выбранного поставщика</li>
              <li>Выберите микрофон, а в блоке <strong>Речь</strong> выберите динамики или наушники как <strong>Устройство воспроизведения по умолчанию</strong>. <strong>Перевод, который слышит собеседник</strong> идёт в виртуальный микрофон <code>Sokuji_Virtual_Mic</code>, который вы выбираете как микрофон в приложении для встреч</li>
              <li>Протестируйте звук, чтобы убедиться, что всё работает</li>
            </ol>`
          }
        ],
        success: "Sokuji готов к использованию! Вы можете начать перевод в реальном времени, нажав кнопку начала сеанса."
      }
    ],
    troubleshooting: {
      title: "Устранение неполадок",
      issues: [
        {
          title: "Проблемы со звуком",
          content: `Если у вас проблемы со звуком:
          <ul>
            <li>Убедитесь, что PulseAudio или PipeWire запущен: <code>systemctl --user status pipewire</code></li>
            <li>Проверьте разрешения звука: <code>groups $USER</code> должен включать 'audio'</li>
            <li>Перезапустите звуковую службу: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Отказ в доступе",
          content: `Если вы получаете ошибки «Permission denied»:
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Затем выйдите из системы и войдите снова, чтобы изменения вступили в силу.`
        },
        {
          title: "Отсутствующие зависимости",
          content: `Установите необходимые библиотеки:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage не запускается",
          content: `Если AppImage не запускается, установите FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Для более новых систем (Ubuntu 22.04+) может потребоваться:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  de: {
    pageTitle: "Linux-Installationsanleitung",
    langLabel: "Sprache:",
    backLink: "← Zurueck zur Dokumentation",
    tocTitle: "Inhaltsverzeichnis",
    mobileJumpTo: "Zum Abschnitt springen...",
    overview: "Diese Anleitung fuehrt Sie durch die Installation von Sokuji auf Linux-Systemen. Sokuji ist als .deb-Paket fuer Debian/Ubuntu-basierte Distributionen und als AppImage fuer universelle Kompatibilitaet verfuegbar.",
    requirements: {
      title: "Systemanforderungen",
      items: [
        "Ubuntu 20.04 oder spaeter, Debian 10 oder spaeter, oder aequivalente Distribution",
        "64-Bit-Prozessor",
        "Mindestens 4 GB RAM (8 GB empfohlen)",
        "100 MB verfuegbarer Festplattenspeicher",
        "PulseAudio oder PipeWire fuer Audio-Unterstuetzung",
        "Internetverbindung fuer KI-Dienste"
      ]
    },
    steps: [
      {
        title: "Schritt 1: Sokuji herunterladen",
        content: `Besuchen Sie die <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">offizielle GitHub-Releases-Seite</a>, um die neueste Version von Sokuji fuer Linux herunterzuladen.`,
        subsections: [
          {
            title: "Verfuegbare Pakete",
            content: `<ul>
              <li><strong>.deb-Paket</strong> - Fuer Debian/Ubuntu-basierte Distributionen</li>
              <li><strong>.AppImage</strong> - Universelles Paket fuer alle Linux-Distributionen</li>
              <li><strong>.rpm-Paket</strong> - Fuer Fedora/RHEL-basierte Distributionen (falls verfuegbar)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Schritt 2: .deb-Paket installieren (Ubuntu/Debian)",
        content: `Wenn Sie Ubuntu, Debian oder deren Derivate verwenden, wird das .deb-Paket empfohlen.`,
        subsections: [
          {
            title: "Methode 1: Ueber die grafische Oberflaeche",
            content: `<ol>
              <li>Oeffnen Sie Ihren Dateimanager und navigieren Sie zum Downloads-Ordner</li>
              <li>Doppelklicken Sie auf die Datei <code>sokuji_*.deb</code></li>
              <li>Klicken Sie auf "Installieren" im sich oeffnenden Software-Center</li>
              <li>Geben Sie Ihr Passwort ein, wenn Sie dazu aufgefordert werden</li>
            </ol>`
          },
          {
            title: "Methode 2: Ueber das Terminal",
            content: `Oeffnen Sie das Terminal und fuehren Sie die folgenden Befehle aus:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Bei Abhaengigkeitsproblemen beheben Sie diese mit:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Schritt 3: AppImage installieren (Universell)",
        content: `AppImage funktioniert auf allen Linux-Distributionen und erfordert keine Installation.`,
        subsections: [
          {
            title: "AppImage einrichten",
            content: `<ol>
              <li>Laden Sie die AppImage-Datei von der Releases-Seite herunter</li>
              <li>Machen Sie sie ausfuehrbar:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Starten Sie die Anwendung:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Optional: Systemintegration",
            content: `Um die AppImage mit Ihrem System zu integrieren:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Jetzt koennen Sie Sokuji von ueberall aus starten, indem Sie <code>sokuji</code> im Terminal eingeben.`
          }
        ]
      },
      {
        title: "Schritt 4: Ersteinrichtung",
        content: `Beim ersten Start von Sokuji muessen Sie es konfigurieren:`,
        subsections: [
          {
            title: "Ersteinrichtung",
            content: `<ol>
              <li>Starten Sie Sokuji ueber das Anwendungsmenue oder Terminal</li>
              <li>Waehlen Sie Ihre bevorzugte Oberflaechensprache</li>
              <li>Konfigurieren Sie Ihren KI-Anbieter (OpenAI, Google Gemini usw.)</li>
              <li>Geben Sie Ihren API-Schluessel fuer den ausgewaehlten Anbieter ein</li>
              <li>Wählen Sie Ihr Mikrofon, und wählen Sie im Block <strong>Sprache</strong> Ihre Lautsprecher oder Kopfhörer als <strong>Standard-Wiedergabegerät</strong>. <strong>Übersetzung, die das Gegenüber hört</strong> geht in das virtuelle Mikrofon <code>Sokuji_Virtual_Mic</code>, das Sie in Ihrer Meeting-App als Mikrofon wählen</li>
              <li>Testen Sie das Audio, um sicherzustellen, dass alles funktioniert</li>
            </ol>`
          }
        ],
        success: "Sokuji ist jetzt einsatzbereit! Sie koennen die Echtzeit-Uebersetzung starten, indem Sie auf die Schaltflaeche zum Starten der Sitzung klicken."
      }
    ],
    troubleshooting: {
      title: "Fehlerbehebung",
      issues: [
        {
          title: "Audio-Probleme",
          content: `Wenn Sie Audio-Probleme haben:
          <ul>
            <li>Stellen Sie sicher, dass PulseAudio oder PipeWire laeuft: <code>systemctl --user status pipewire</code></li>
            <li>Ueberpruefen Sie die Audio-Berechtigungen: <code>groups $USER</code> sollte 'audio' enthalten</li>
            <li>Starten Sie den Audio-Dienst neu: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Zugriff verweigert",
          content: `Wenn Sie "Permission denied"-Fehler erhalten:
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Melden Sie sich dann ab und wieder an, damit die Aenderungen wirksam werden.`
        },
        {
          title: "Fehlende Abhaengigkeiten",
          content: `Installieren Sie die erforderlichen Bibliotheken:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage startet nicht",
          content: `Wenn AppImage nicht startet, installieren Sie FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Fuer neuere Systeme (Ubuntu 22.04+) benoetigen Sie moeglicherweise:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  uk: {
    pageTitle: "Посібник зі встановлення на Linux",
    langLabel: "Мова:",
    backLink: "← Повернутися до документації",
    tocTitle: "Зміст",
    mobileJumpTo: "Перейти до розділу...",
    overview: "Цей посібник допоможе вам встановити Sokuji на системи Linux. Sokuji доступний як пакет .deb для дистрибутивів на базі Debian/Ubuntu та як AppImage для універсальної сумісності.",
    requirements: {
      title: "Системні вимоги",
      items: [
        "Ubuntu 20.04 або новіша, Debian 10 або новіша, або еквівалентний дистрибутив",
        "64-розрядний процесор",
        "Мінімум 4 ГБ оперативної памʼяті (рекомендовано 8 ГБ)",
        "100 МБ вільного дискового простору",
        "PulseAudio або PipeWire для підтримки звуку",
        "Підключення до інтернету для служб ШІ"
      ]
    },
    steps: [
      {
        title: "Крок 1: Завантажити Sokuji",
        content: `Відвідайте <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">офіційну сторінку релізів на GitHub</a>, щоб завантажити останню версію Sokuji для Linux.`,
        subsections: [
          {
            title: "Доступні пакети",
            content: `<ul>
              <li><strong>Пакет .deb</strong> - Для дистрибутивів на базі Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Універсальний пакет для всіх дистрибутивів Linux</li>
              <li><strong>Пакет .rpm</strong> - Для дистрибутивів на базі Fedora/RHEL (якщо доступний)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Крок 2: Встановлення пакету .deb (Ubuntu/Debian)",
        content: `Якщо ви використовуєте Ubuntu, Debian або їх похідні, рекомендується пакет .deb.`,
        subsections: [
          {
            title: "Спосіб 1: Через графічний інтерфейс",
            content: `<ol>
              <li>Відкрийте файловий менеджер та перейдіть до папки Завантаження</li>
              <li>Двічі клацніть на файл <code>sokuji_*.deb</code></li>
              <li>Натисніть "Встановити" у Центрі програм, що відкриється</li>
              <li>Введіть пароль при запиті</li>
            </ol>`
          },
          {
            title: "Спосіб 2: Через термінал",
            content: `Відкрийте Термінал та виконайте наступні команди:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Якщо виникнуть проблеми із залежностями, виправте їх командою:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Крок 3: Встановлення AppImage (Універсальний)",
        content: `AppImage працює на всіх дистрибутивах Linux та не потребує встановлення.`,
        subsections: [
          {
            title: "Налаштування AppImage",
            content: `<ol>
              <li>Завантажте файл AppImage зі сторінки релізів</li>
              <li>Зробіть його виконуваним:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Запустіть програму:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Необовʼязково: Інтеграція з системою",
            content: `Для інтеграції AppImage з системою:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Тепер ви можете запускати Sokuji з будь-якого місця, ввівши <code>sokuji</code> у терміналі.`
          }
        ]
      },
      {
        title: "Крок 4: Налаштування першого запуску",
        content: `При першому запуску Sokuji необхідно його налаштувати:`,
        subsections: [
          {
            title: "Початкове налаштування",
            content: `<ol>
              <li>Запустіть Sokuji з меню програм або терміналу</li>
              <li>Оберіть бажану мову інтерфейсу</li>
              <li>Налаштуйте постачальника ШІ (OpenAI, Google Gemini тощо)</li>
              <li>Введіть API-ключ для обраного постачальника</li>
              <li>Оберіть мікрофон, а в блоці <strong>Мовлення</strong> оберіть динаміки або навушники як <strong>Пристрій відтворення за замовчуванням</strong>. <strong>Переклад, який чує співрозмовник</strong> іде у віртуальний мікрофон <code>Sokuji_Virtual_Mic</code>, який ви обираєте як мікрофон у програмі для зустрічей</li>
              <li>Протестуйте звук, щоб переконатися, що все працює</li>
            </ol>`
          }
        ],
        success: "Sokuji готовий до використання! Ви можете розпочати переклад у реальному часі, натиснувши кнопку початку сеансу."
      }
    ],
    troubleshooting: {
      title: "Усунення несправностей",
      issues: [
        {
          title: "Проблеми зі звуком",
          content: `Якщо у вас проблеми зі звуком:
          <ul>
            <li>Переконайтеся, що PulseAudio або PipeWire запущено: <code>systemctl --user status pipewire</code></li>
            <li>Перевірте дозволи звуку: <code>groups $USER</code> має включати 'audio'</li>
            <li>Перезапустіть звукову службу: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Відмова в доступі",
          content: `Якщо ви отримуєте помилки "Permission denied":
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Потім вийдіть із системи та увійдіть знову, щоб зміни набули чинності.`
        },
        {
          title: "Відсутні залежності",
          content: `Встановіть необхідні бібліотеки:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage не запускається",
          content: `Якщо AppImage не запускається, встановіть FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Для новіших систем (Ubuntu 22.04+) може знадобитися:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  },
  it: {
    pageTitle: "Guida all'installazione su Linux",
    langLabel: "Lingua:",
    backLink: "← Torna alla documentazione",
    tocTitle: "Indice",
    mobileJumpTo: "Vai alla sezione...",
    overview: "Questa guida vi accompagnera nell'installazione di Sokuji sui sistemi Linux. Sokuji e disponibile come pacchetto .deb per le distribuzioni basate su Debian/Ubuntu e come AppImage per la compatibilita universale.",
    requirements: {
      title: "Requisiti di sistema",
      items: [
        "Ubuntu 20.04 o successivo, Debian 10 o successivo, o distribuzione equivalente",
        "Processore a 64 bit",
        "4 GB di RAM minimo (8 GB consigliati)",
        "100 MB di spazio su disco disponibile",
        "PulseAudio o PipeWire per il supporto audio",
        "Connessione Internet per i servizi di IA"
      ]
    },
    steps: [
      {
        title: "Passaggio 1: Scaricare Sokuji",
        content: `Visitate la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina ufficiale delle release su GitHub</a> per scaricare l'ultima versione di Sokuji per Linux.`,
        subsections: [
          {
            title: "Pacchetti disponibili",
            content: `<ul>
              <li><strong>Pacchetto .deb</strong> - Per le distribuzioni basate su Debian/Ubuntu</li>
              <li><strong>.AppImage</strong> - Pacchetto universale per tutte le distribuzioni Linux</li>
              <li><strong>Pacchetto .rpm</strong> - Per le distribuzioni basate su Fedora/RHEL (se disponibile)</li>
            </ul>`
          }
        ]
      },
      {
        title: "Passaggio 2: Installare il pacchetto .deb (Ubuntu/Debian)",
        content: `Se utilizzate Ubuntu, Debian o le loro derivate, si consiglia il pacchetto .deb.`,
        subsections: [
          {
            title: "Metodo 1: Tramite interfaccia grafica",
            content: `<ol>
              <li>Aprite il gestore dei file e navigate alla cartella Download</li>
              <li>Fate doppio clic sul file <code>sokuji_*.deb</code></li>
              <li>Cliccate su "Installa" nel Centro Software che si apre</li>
              <li>Inserite la password quando richiesto</li>
            </ol>`
          },
          {
            title: "Metodo 2: Tramite terminale",
            content: `Aprite il Terminale ed eseguite i seguenti comandi:
            <pre><code>cd ~/Downloads
sudo dpkg -i sokuji_*.deb</code></pre>
            Se riscontrate problemi di dipendenze, correggeteli con:
            <pre><code>sudo apt-get install -f</code></pre>`
          }
        ]
      },
      {
        title: "Passaggio 3: Installare AppImage (Universale)",
        content: `AppImage funziona su tutte le distribuzioni Linux e non richiede installazione.`,
        subsections: [
          {
            title: "Configurare AppImage",
            content: `<ol>
              <li>Scaricate il file AppImage dalla pagina delle release</li>
              <li>Rendetelo eseguibile:
                <pre><code>chmod +x sokuji-*.AppImage</code></pre>
              </li>
              <li>Eseguite l'applicazione:
                <pre><code>./sokuji-*.AppImage</code></pre>
              </li>
            </ol>`
          },
          {
            title: "Opzionale: Integrazione di sistema",
            content: `Per integrare l'AppImage con il vostro sistema:
            <pre><code>mkdir -p ~/.local/bin
cp sokuji-*.AppImage ~/.local/bin/sokuji
chmod +x ~/.local/bin/sokuji</code></pre>
            Ora potete eseguire Sokuji da qualsiasi posizione digitando <code>sokuji</code> nel terminale.`
          }
        ]
      },
      {
        title: "Passaggio 4: Configurazione del primo avvio",
        content: `Al primo avvio di Sokuji, dovrete configurarlo:`,
        subsections: [
          {
            title: "Configurazione iniziale",
            content: `<ol>
              <li>Avviate Sokuji dal menu delle applicazioni o dal terminale</li>
              <li>Selezionate la vostra lingua dell'interfaccia preferita</li>
              <li>Configurate il vostro fornitore di IA (OpenAI, Google Gemini, ecc.)</li>
              <li>Inserite la vostra chiave API per il fornitore selezionato</li>
              <li>Selezionate il microfono e, nel blocco <strong>Voce</strong>, scegliete i vostri altoparlanti o le cuffie come <strong>Dispositivo di riproduzione predefinito</strong>. <strong>Traduzione che sente l'altra parte</strong> va nel microfono virtuale <code>Sokuji_Virtual_Mic</code>, che scegliete come microfono nell'app per riunioni</li>
              <li>Testate l'audio per assicurarvi che tutto funzioni</li>
            </ol>`
          }
        ],
        success: "Sokuji e ora pronto per l'uso! Potete avviare la traduzione in tempo reale cliccando sul pulsante di avvio sessione."
      }
    ],
    troubleshooting: {
      title: "Risoluzione dei problemi",
      issues: [
        {
          title: "Problemi audio",
          content: `Se riscontrate problemi audio:
          <ul>
            <li>Assicuratevi che PulseAudio o PipeWire sia in esecuzione: <code>systemctl --user status pipewire</code></li>
            <li>Verificate i permessi audio: <code>groups $USER</code> dovrebbe includere 'audio'</li>
            <li>Riavviate il servizio audio: <code>systemctl --user restart pipewire</code></li>
          </ul>`
        },
        {
          title: "Permesso negato",
          content: `Se ottenete errori "Permission denied":
          <pre><code>sudo usermod -a -G audio $USER</code></pre>
          Quindi disconnettetevi e riconnettetevi per rendere effettive le modifiche.`
        },
        {
          title: "Dipendenze mancanti",
          content: `Installate le librerie necessarie:
          <pre><code>sudo apt-get update
sudo apt-get install libgtk-3-0 libnotify4 libnss3 libxss1 \\
    libxtst6 xdg-utils libatspi2.0-0 libdrm2 libgbm1</code></pre>`
        },
        {
          title: "AppImage non si avvia",
          content: `Se AppImage non si avvia, installate FUSE:
          <pre><code>sudo apt-get install fuse libfuse2</code></pre>
          Per sistemi piu recenti (Ubuntu 22.04+), potreste aver bisogno di:
          <pre><code>sudo apt-get install libfuse2t64</code></pre>`
        }
      ]
    },
  }
};

export function LinuxInstall() {
  const { locale } = useI18n();
  const lp = useLocalizedPath();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Get current language data
  const currentLang = locale.startsWith('zh') ? 'zh' :
                      locale.startsWith('ja') ? 'ja' :
                      locale.startsWith('ko') ? 'ko' :
                      locale.startsWith('es') ? 'es' :
                      locale.startsWith('fr') ? 'fr' :
                      locale.startsWith('ar') ? 'ar' :
                      locale.startsWith('pt') ? 'pt' :
                      locale.startsWith('ru') ? 'ru' :
                      locale.startsWith('de') ? 'de' :
                      locale.startsWith('uk') ? 'uk' :
                      locale.startsWith('it') ? 'it' : 'en';
  const t = translations[currentLang] || translations.en;

  // Handle clicks on content that may contain images
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).src;
      setLightboxImage(src);
    }
  }, []);

  return (
    <div className="docs-content install-page">
      {/* Back link */}
      <Link to={lp('/docs')} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <p className="install-page__overview">{t.overview}</p>

      {/* Download button */}
      <a
        href="https://github.com/kizuna-ai-lab/sokuji/releases/latest"
        target="_blank"
        rel="noopener noreferrer"
        className="install-page__download-btn"
      >
        <Download size={20} />
        {currentLang === 'zh' ? '下载 Linux 版本' :
         currentLang === 'ja' ? 'Linux版をダウンロード' :
         currentLang === 'ko' ? 'Linux용 다운로드' :
         currentLang === 'es' ? 'Descargar para Linux' :
         currentLang === 'fr' ? 'Telecharger pour Linux' :
         currentLang === 'ar' ? 'تحميل لنظام Linux' :
         currentLang === 'pt' ? 'Baixar para Linux' :
         currentLang === 'ru' ? 'Скачать для Linux' :
         currentLang === 'de' ? 'Fuer Linux herunterladen' :
         currentLang === 'uk' ? 'Завантажити для Linux' :
         currentLang === 'it' ? 'Scarica per Linux' :
         'Download for Linux'}
        <ExternalLink size={16} />
      </a>

      {/* System Requirements */}
      <div className="install-page__requirements">
        <h3>{t.requirements.title}</h3>
        <ul>
          {t.requirements.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Installation Steps */}
      <h2>{currentLang === 'zh' ? '安装步骤' :
           currentLang === 'ja' ? 'インストール手順' :
           currentLang === 'ko' ? '설치 단계' :
           currentLang === 'es' ? 'Pasos de instalacion' :
           currentLang === 'fr' ? "Etapes d'installation" :
           currentLang === 'ar' ? 'خطوات التثبيت' :
           currentLang === 'pt' ? 'Etapas de instalacao' :
           currentLang === 'ru' ? 'Шаги установки' :
           currentLang === 'de' ? 'Installationsschritte' :
           currentLang === 'uk' ? 'Кроки встановлення' :
           currentLang === 'it' ? "Passaggi dell'installazione" :
           'Installation Steps'}</h2>

      {t.steps.map((step, stepIndex) => (
        <div key={stepIndex} className="install-page__step" onClick={handleContentClick}>
          <h3>{step.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: step.content }} />

          {step.subsections?.map((sub, subIndex) => (
            <div key={subIndex}>
              <h4>{sub.title}</h4>
              <div dangerouslySetInnerHTML={{ __html: sub.content }} />
            </div>
          ))}

          {step.warning && (
            <div className="install-page__warning">{step.warning}</div>
          )}

          {step.info && (
            <div className="install-page__info">{step.info}</div>
          )}

          {step.success && (
            <div className="install-page__success">{step.success}</div>
          )}
        </div>
      ))}

      {/* Troubleshooting */}
      <div className="install-page__troubleshooting">
        <h2>{t.troubleshooting.title}</h2>

        {t.troubleshooting.issues.map((issue, index) => (
          <div key={index} className="install-page__issue">
            <h3>{issue.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: issue.content }} />
          </div>
        ))}
      </div>

      {/* Lightbox for images */}
      <Lightbox
        src={lightboxImage || ''}
        alt="Screenshot"
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
}
